import { fetchUtils } from "react-admin";
import { stringify } from "query-string";
import { DataProvider } from "react-admin";
import { HttpClient } from "./http.client";
//import { AppDataProvider } from "./app.data.provider";
import { getConfig } from "../config/app.config";
import { getBaseUrl as getFrameworkBaseUrl } from "../config/api.config";
import { blobToBase64, blobToRawBase64, pruneHar } from "./encoding/har.utils";
import { resourceRegistry } from "../../services/resource-registry";

const dp = "-dp";

// Platform resources that need the -dp suffix
// Generated data entities (consumer, product, order, etc.) don't need it
const platformResources = new Set([
  'application', 'application-deployment', 'tenant', 'brand', 'location', 'device', 'api-key', 'environment', 'integration',
  'schema', 'datamodel', 'appfunction', 'userexperience',
  'aiassistant', 'aiassistantinstance', 'aifile', 'aifileinstance', 
  'aifilestore', 'aithread', 'aimessage',
  'analyticsdashboard', 'analyticsdashboards', 'analytics-dashboards', 'uifile', 'uifilestore',
  'connection', 'authentication-provider'
]);

// Helper to get full resource path (adds /data prefix for generated entities)
const getResourcePath = (resource: string): string => {
  // Check platform resources FIRST - framework entities take precedence over generated ones
  const isPlatformResource = platformResources.has(resource.toLowerCase());
  console.log(`[DataProvider] Resource "${resource}" isPlatformResource: ${isPlatformResource}`);
  
  if (isPlatformResource) {
    const path = `${resource}${dp}`;
    console.log(`[DataProvider] Resource "${resource}" using platform path: ${path}`);
    return path;
  }
  
  // Then check registry for custom API paths (only for non-platform resources)
  const registeredPath = resourceRegistry.getApiPath(resource);
  if (registeredPath) {
    console.log(`[DataProvider] Resource "${resource}" using registry path: ${registeredPath}`);
    return registeredPath;
  }
  
  // Finally, default to data model path for generated entities
  // Path format: /data-models/{dataModelKey}/{resource}
  const dataModelKey = resourceRegistry.getDataModelKey(resource) || 'core-data-model';
  const path = `data-models/${dataModelKey}/${resource}`;
  console.log(`[DataProvider] Resource "${resource}" using data model path: ${path}`);
  return path;
};

export const createDataProvider = (httpClient: HttpClient) => {

  /**
   * Determines if an array contains file upload objects (not regular data)
   * File upload objects have: rawFile (File), src (string), or source (string) properties
   * Regular data arrays contain: primitives, plain objects, or entity references
   */
  const isFileUploadArray = (arr: any[]): boolean => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return false;
    }

    // Sample the first few items (up to 3) to determine array type
    const sampleSize = Math.min(3, arr.length);
    const samples = arr.slice(0, sampleSize);

    return samples.some(item => {
      // Primitives (strings, numbers, booleans) are NOT file uploads
      if (typeof item !== 'object' || item === null) {
        return false;
      }

      // Check for file upload signatures
      return (
        item.rawFile instanceof File ||           // Local file upload
        (item.src && typeof item.src === 'string' && item.source) ||  // Cloud file with source
        (item.rawFile && typeof item.rawFile === 'object')  // Serialized file object
      );
    });
  };

  const convertFileToBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /** True if a file should be treated as a HAR */
  const isHarFile = (file: File) =>
    file.name.toLowerCase().endsWith(".har") ||
    file.type === "application/json-har" ||    // Chrome devtools mime
    (file.type === "application/json" && file.name.toLowerCase().includes(".har"));

  /** Generic fallback for non‑HAR uploads - returns base64 with metadata for proper server-side parsing */
  const fileToBase64 = (file: File) => blobToBase64(file);

  const extractFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
  };

  const preparePayloadWithFiles = async (data: any) => {
    console.log('preparePayloadWithFiles input:', data);
    console.log('Files structure:', JSON.stringify(data.files, null, 2));
    const processed = { ...data };

    for (const [key, value] of Object.entries(data)) {
      // if the key has a dot in it, we need to use the dot to set the target attribute and then an optional name for a nested attribute
      const dotIndex = key.indexOf('.');
      const targetAttribute = dotIndex !== -1 ? key.substring(0, dotIndex) : key;
      const nestedAttribute = dotIndex !== -1 ? key.substring(dotIndex + 1) : null;
      console.log('targetAttribute', targetAttribute);
      console.log('nestedAttribute', nestedAttribute);

      // Handle nested objects that might contain file arrays (e.g., file: {fileContent: [...]})
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value as any).rawFile) {
        console.log('Processing nested object', key, value);
        const nestedProcessed = { ...value };

        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          if (Array.isArray(nestedValue)) {
            console.log('Found nested array', nestedKey, nestedValue);
            
            // Use detection to determine if this is a file upload array
            if (!isFileUploadArray(nestedValue)) {
              console.log(`⏭️  SKIPPED: "${nestedKey}" array is NOT a file upload array (contains ${nestedValue.length} items) - preserving original data`);
              continue;
            }
            
            console.log(`📎 PROCESSING: "${nestedKey}" array detected as file uploads (${nestedValue.length} items)`);
            const processedFiles = [];

            for (const item of nestedValue) {
              const fileItem = item as { rawFile?: File; title?: string; src?: string; source?: string; type?: string };

              console.log('Processing nested fileItem', fileItem);

              // Skip if this is already a cloud file (OneDrive/SharePoint)
              if (fileItem.source === 'onedrive' || (!fileItem.rawFile && fileItem.src)) {
                const filename = fileItem.title || 'cloud-file';
                // Create proper AIFileInstance object for cloud files
                processedFiles.push({
                  fileContent: fileItem.src, // src should contain base64 data
                  name: filename,
                  fileExtension: extractFileExtension(filename),
                  type: fileItem.type || 'application/octet-stream',
                  source: fileItem.source || 'cloud'
                });
                continue;
              }

              // Process local file uploads (has rawFile)
              if (fileItem?.rawFile) {
                console.log('Nested rawFile type:', typeof fileItem.rawFile, fileItem.rawFile);

                // Check if rawFile is actually a File instance
                if (fileItem.rawFile instanceof File) {
                  const file = fileItem.rawFile;

                  if (isHarFile(file)) {
                    const text = await file.text();
                    const pruned = pruneHar(JSON.parse(text));
                    const blob = new Blob(
                      [JSON.stringify(pruned)],
                      { type: "application/json" }
                    );
                    const base64Data = await blobToRawBase64(blob);
                    //const base64Data = await blobToBase64(blob);
                    const filename = fileItem.title || file.name.replace(/\.har$/i, ".pruned.har");

                    // Create proper AIFileInstance object for HAR files
                    processedFiles.push({
                      fileContent: base64Data,
                      name: filename,
                      fileExtension: extractFileExtension(filename),
                      type: 'application/json',
                      source: 'local'
                    });
                    continue;
                  }

                  console.log('Processing nested ordinary local file', file);
                  const base64Data = await fileToBase64(file);
                  const filename = fileItem.title || file.name;
                  // Create proper AIFileInstance object
                  processedFiles.push({
                    fileContent: base64Data,
                    name: filename,
                    fileExtension: extractFileExtension(filename),
                    type: file.type || 'application/octet-stream',
                    source: 'local'
                  });
                } else {
                  // Handle case where rawFile is not a File instance (might be serialized)
                  console.warn('rawFile is not a File instance:', fileItem.rawFile);
                  console.warn('This might indicate a serialization issue or invalid file data');

                  // If it has a path or name, try to create a placeholder AIFileInstance
                  const rawFileAny = fileItem.rawFile as any;
                  if (rawFileAny?.path || rawFileAny?.name) {
                    const filename = fileItem.title || rawFileAny?.path || rawFileAny?.name || 'Unknown File';
                    processedFiles.push({
                      fileContent: null, // No actual content available
                      name: filename,
                      fileExtension: extractFileExtension(filename),
                      type: 'application/octet-stream',
                      source: 'local',
                      error: 'Invalid file object - no content available'
                    });
                  }
                }
              }
            }

            console.log('Nested processedFiles (AIFileInstance objects with name, fileExtension, fileContent):', processedFiles);
            // Always return array of AIFileInstance objects
            nestedProcessed[nestedKey] = processedFiles;
          }
        }

        processed[targetAttribute] = nestedProcessed;
        continue;
      }

      // Handle arrays of files (multiple file uploads)
      if (Array.isArray(value)) {
        console.log('isArray', value);
        
        // Use detection to determine if this is a file upload array
        if (!isFileUploadArray(value)) {
          console.log(`⏭️  SKIPPED: "${key}" array is NOT a file upload array (contains ${value.length} items) - preserving original data`);
          continue;
        }
        
        console.log(`📎 PROCESSING: "${key}" array detected as file uploads (${value.length} items)`);
        const processedFiles = [];

        for (const item of value) {
          const fileItem = item as { rawFile?: File; title?: string; src?: string; source?: string; type?: string };

          console.log('fileItem', fileItem);

          // Skip if this is already a cloud file (OneDrive/SharePoint)
          if (fileItem.source === 'onedrive' || (!fileItem.rawFile && fileItem.src)) {
            const filename = fileItem.title || 'cloud-file';
            // Create proper AIFileInstance object for cloud files
            processedFiles.push({
              fileContent: fileItem.src, // src should contain base64 data
              name: filename,
              fileExtension: extractFileExtension(filename),
              type: fileItem.type || 'application/octet-stream',
              source: fileItem.source || 'cloud'
            });
            continue;
          }

          // Process local file uploads (has rawFile)
          if (fileItem?.rawFile) {
            console.log('Array rawFile type:', typeof fileItem.rawFile, fileItem.rawFile);

            if (fileItem.rawFile instanceof File) {
              const file = fileItem.rawFile;

              // ── HAR branch ────────────────────────────────────────────────
              if (isHarFile(file)) {
                const text = await file.text();                  // 1. read
                const pruned = pruneHar(JSON.parse(text));       // 2. prune
                const blob = new Blob(
                  [JSON.stringify(pruned)],
                  { type: "application/json" }
                );                                               // 3. re‑blob
                const base64Data = await blobToRawBase64(blob);     // 4. base‑64
                const filename = fileItem.title || file.name.replace(/\.har$/i, ".pruned.har");

                processedFiles.push({
                  fileContent: base64Data,
                  name: filename,
                  fileExtension: extractFileExtension(filename),
                  type: 'application/json',
                  source: 'local'
                });
                continue;
              }

              // ── Ordinary local file branch ─────────────────────────────────────
              console.log('ordinary local file in array', file);
              const base64Data = await fileToBase64(file);
              const filename = fileItem.title || file.name;
              processedFiles.push({
                fileContent: base64Data,
                name: filename,
                fileExtension: extractFileExtension(filename),
                type: file.type || 'application/octet-stream',
                source: 'local'
              });
            } else {
              // Handle invalid rawFile in array
              console.warn('Array rawFile is not a File instance:', fileItem.rawFile);
              const rawFileAny = fileItem.rawFile as any;
              if (rawFileAny?.path || rawFileAny?.name) {
                const filename = fileItem.title || rawFileAny?.path || rawFileAny?.name || 'Unknown File';
                processedFiles.push({
                  fileContent: null,
                  name: filename,
                  fileExtension: extractFileExtension(filename),
                  type: 'application/octet-stream',
                  source: 'local',
                  error: 'Invalid file object - no content available'
                });
              }
            }
          }
        }
        console.log('processedFiles (AIFileInstance objects with name, fileExtension, fileContent):', processedFiles);
        processed[targetAttribute] = processedFiles;
        continue;
      }

      // Handle single file (backward compatibility)
      const maybe = value as { rawFile?: File; title?: string; src?: string; source?: string };

      console.log('maybe', maybe);

      // Skip cloud files that are already processed
      if (maybe?.source === 'onedrive' || (!maybe?.rawFile && maybe?.src)) {
        continue;
      }

      if (!(maybe?.rawFile instanceof File)) {
        console.log('not a file');
        continue;
      }

      const file = maybe.rawFile;

      // ── HAR branch ────────────────────────────────────────────────
      if (isHarFile(file)) {
        console.log('isHarFile');
        const text = await file.text();                  // 1. read
        const pruned = pruneHar(JSON.parse(text));       // 2. prune
        const blob = new Blob(
          [JSON.stringify(pruned)],
          { type: "application/json" }
        );                                               // 3. re‑blob
        processed[targetAttribute] = await blobToRawBase64(blob);  // 4. base‑64
        processed[`${targetAttribute}Name`] =
          maybe.title || file.name.replace(/\.har$/i, ".pruned.har");
        continue;
      }
      console.log('ordinary file branch 2');
      // ── Ordinary file branch ─────────────────────────────────────
      processed[targetAttribute] = await fileToBase64(file);
      processed[`${targetAttribute}Name`] = maybe.title || file.name;
    }

    return processed;
  };

  const dataProvider: DataProvider = {

    getBaseUrl: () => {
      // Try to get from app config first (existing behavior)
      const configUrl = getConfig('VITE_BASE_URL');
      if (configUrl) {
        return configUrl;
      }
      
      // Fallback to framework utility for consistent URL resolution
      return getFrameworkBaseUrl();
    },

    getOneWithSelect: async (resource, id, selectFields: string[]) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const selectParam = selectFields.join(',');
      const resourcePath = getResourcePath(resource);
      const { json } = await httpClient(`${csApiUrl}/${resourcePath}/getByIdWithSelect/${id}?select=${selectParam}`);

      if (!json)
        return { data: {} };

      if (json.id) {
        return { ...json, [resource + 'Id']: json.id, id: json._id };
      }
      else {
        return { ...json, id: json._id };
      }
    },

    getList: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify(params.filter),
      };
      const resourcePath = getResourcePath(resource);
      const url = `${csApiUrl}/${resourcePath}?${stringify(query)}`;
      const resourceIdName = `${resource}Id`;
      const { json } = await httpClient(url);
      const list = json?.result;
      const pagination = json?.paginationResponse;

      if (!list)
        return { data: [], total: 0 };

      const ret = (
        {
          data: list.map((resource_1: { _id: any; id: any; }) => ({ ...resource_1, [resourceIdName]: resource_1.id, id: resource_1._id })),
          total: pagination.total
        });

      return ret;
    },

    getOne: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const resourcePath = getResourcePath(resource);
      const { json } = await httpClient(`${csApiUrl}/${resourcePath}/${params.id}`);
      let ret;

      if (!json)
        return { data: {} };

      if (json.id) {
        ret = ({
          data: { ...json, [resource + 'Id']: json.id, id: json._id },
        });
      }
      else {
        ret = ({
          data: { ...json, id: json._id },
        });
      }
      return ret;
    },


    getOneById: async (resource, id) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const resourcePath = getResourcePath(resource);
      const { json } = await httpClient(`${csApiUrl}/${resourcePath}/getById/${id}`);
      let ret;

      if (!json)
        return { data: {} };

      if (json.id) {
        ret = ({
          data: { ...json, [resource + 'Id']: json.id, id: json._id },
        });
      }
      else {
        ret = ({
          data: { ...json, id: json._id },
        });
      }
      return ret.data;

      //return ret;
    },


    getMany: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const query = {
        filter: JSON.stringify({ ids: params.ids }),
      };
      const resourcePath = getResourcePath(resource);
      const url = `${csApiUrl}/${resourcePath}?${stringify(query)}`;

      const { json } = await httpClient(url);
      const list = json?.result;
      const pagination = json?.paginationResponse;
      const resourceIdName = `${resource}Id`;

      if (!list)
        return { data: [] };

      const ret = (
        {
          data: list.map((resource_1: { _id: any; id: any; }) => ({ ...resource_1, [resourceIdName]: resource_1.id, id: resource_1._id })),
        });

      return ret;
      /*
      return httpClient(url).then(({ json }) => ({
        data: json.map((resource: { _id: any }) => ({
          ...resource,
          [resource + 'Id']: resource._id,
          id: resource._id,
        })),
      }));
      */
    },

    getManyReference: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      };
      const resourcePath = getResourcePath(resource);
      const url = `${csApiUrl}/${resourcePath}?${stringify(query)}`;

      const resourceIdName = `${resource}Id`;
      const { json } = await httpClient(url);
      const list = json.result;
      const pagination = json.paginationResponse;

      const ret = (
        {
          data: list.map((resource_1: { _id: any; id: any; }) => ({ ...resource_1, [resourceIdName]: resource_1.id, id: resource_1._id })),
          total: pagination.total
        });

      return ret;
      /*
      
          return httpClient(url).then(({ headers, json }) => ({
            data: json.map((resource: { _id: any; id: any; }) => ({
              ...resource,
              [resource + 'Id']: resource.id,
              id: resource._id,
            })),
            // total: parseInt(headers.get('content-range').split('/').pop(), 10),
            total: 10,
          }));
          */
    },

    /*
      createWithFileUpload: async (resource, params) => {
        const csApiUrl = dataProvider.getBaseUrl();
        console.log('found a file input');
        console.log('params.data.file', params.data.file);
    
        const formData = new FormData();
        formData.append("file", params.data.file.rawFile);
        const { json } = await httpClient(`${csApiUrl}/ai/createFile`, {
          method: "POST",
          body: formData
        });
    
        return ({
          data: { ...json, [resource + 'Id']: json.id, id: json._id },
         });
      },
      */


    create: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();

      console.log('params.data', params.data);

      const dataWithFiles = await preparePayloadWithFiles(params.data);

      console.log('dataWithFiles (final AIFileInstance structure with name, fileExtension, fileContent):', dataWithFiles);

      const resourcePath = getResourcePath(resource);
      const { json } = await httpClient(`${csApiUrl}/${resourcePath}`, {
        method: "POST",
        body: JSON.stringify(dataWithFiles),
      });
      return ({
        // data: { ...params.data, id: json._id },
        data: { ...json, [resource + 'Id']: json.id, id: json._id },
      });
    },

    update: async (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      
      // Process files in update data (same as create method)
      const dataWithFiles = await preparePayloadWithFiles(params.data);
      const body = { ...dataWithFiles, id: undefined, _id: undefined }

      const resourcePath = getResourcePath(resource);
      const { json } = await httpClient(`${csApiUrl}/${resourcePath}/${params.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      return ({
        data: { ...json, [resource + 'Id']: json.id, id: json._id },
      });
    },

    updateMany: (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const query = {
        filter: JSON.stringify({ id: params.ids }),
      };
      const resourcePath = getResourcePath(resource);
      return httpClient(`${csApiUrl}/${resourcePath}?${stringify(query)}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...json, id: json._id },
      }));
    },

    delete: (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const resourcePath = getResourcePath(resource);
      return httpClient(`${csApiUrl}/${resourcePath}/${params.id}`, {
        method: "DELETE",
      }).then(({ json }) => ({
        data: { ...json, id: json._id },
      }));
    },

    deleteMany: (resource, params) => {
      const csApiUrl = dataProvider.getBaseUrl();
      const query = {
        filter: JSON.stringify({ id: params.ids }),
      };
      const resourcePath = getResourcePath(resource);
      return httpClient(`${csApiUrl}/${resourcePath}?${stringify(query)}`, {
        method: "DELETE",
        body: JSON.stringify(params),
      }).then(({ json }) => ({
        data: json,
      }));
    },
  };

  return dataProvider;
};
